import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import _ from "lodash";
import { Grid, Rating } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactImageGallery from "react-image-gallery";
import "react-medium-image-zoom/dist/styles.css";
import ComponentError from "../../components/404/ComponentError";
import { checkLoggedIn } from "../../utils";
import DeleteIcon from "../../icons/DeleteIcon";
import BuyNowIcon from "../../icons/BuyNowIcon";
import { MoonLoader } from "react-spinners";
import { useLoginModal } from "../../Context/LoginModalContext";

const TRUST_BADGES = [
  { icon: "⚡", title: "Instant delivery", sub: "Code in your inbox in seconds" },
  { icon: "🛡", title: "Secure payments", sub: "3D-Secure · Visa & Mastercard" },
  { icon: "✓", title: "Verified brand", sub: "Sourced from official partners" },
  { icon: "💬", title: "Direct support", sub: "WhatsApp & email channels" },
];

// Split a multi-line text into trimmed non-empty lines
const parseLines = (str) =>
  typeof str === "string"
    ? str.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : [];

// Pair alternating lines as { label, value } — backend sends label then value per line
const parsePackageDetails = (str) => {
  const lines = parseLines(str);
  const out = [];
  for (let i = 0; i < lines.length; i += 2) {
    const label = lines[i];
    const value = lines[i + 1];
    if (label && value) out.push({ label, value });
  }
  return out;
};

const hasContent = (v) => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

const Product = ({
  token,
  carts,
  w_list,
  addToCart,
  addToCartProduct,
  removeFromCart,
  incrementQuantity,
  decrementQuantity,
  ...props
}) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openLoginModal } = useLoginModal();

  const [singleProduct, setSingleProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);

  const inCart = useMemo(
    () => carts.find((cart) => cart.id === singleProduct?.id),
    [carts, singleProduct]
  );

  const inWishList = useMemo(
    () => w_list?.find((wish) => wish.id === singleProduct?.id),
    [w_list, singleProduct]
  );

  const getSingleProductData = async () => {
    setLoading(true);
    if (error) setError(false);
    try {
      const res = await axios.get("get-products", {
        params: { slug },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = {
        ...res?.data?.data?.[0],
        id: res?.data?.data?.[0]?._id,
      };
      setSingleProduct(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSingleProductData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleAddToCart = async () => {
    if (!checkLoggedIn()) {
      openLoginModal();
      return;
    }
    setCartLoading(true);
    await addToCartProduct(singleProduct);
    setCartLoading(false);
  };

  const handleIncrementQuantity = async () => {
    if (!checkLoggedIn()) {
      openLoginModal();
      return;
    }
    setCartLoading(true);
    await incrementQuantity(singleProduct.id);
    setCartLoading(false);
  };

  const handleDecrementQuantity = async () => {
    if (!checkLoggedIn()) {
      openLoginModal();
      return;
    }
    setCartLoading(true);
    if (inCart?.qty === 1) {
      await removeFromCart(singleProduct.id);
    } else {
      await decrementQuantity(singleProduct.id);
    }
    setCartLoading(false);
  };

  const handleBuyNow = async () => {
    if (!checkLoggedIn()) {
      openLoginModal();
      return;
    }
    if (inCart) {
      navigate("/cart");
      return;
    }
    await handleAddToCart();
    navigate("/cart");
  };

  const currencyPrefix =
    typeof window !== "undefined" && localStorage.getItem("currency") === "USD"
      ? "$ "
      : "AED ";

  const descParagraphs = parseLines(singleProduct?.description);
  const includedItems = parseLines(singleProduct?.whatsIncluded);
  const notIncludedItems = parseLines(singleProduct?.whatsNotIncluded);
  const packageRows = parsePackageDetails(singleProduct?.packageDetails);
  const legalNote = singleProduct?.legalNote;

  const hasDetailsBlock =
    includedItems.length > 0 ||
    notIncludedItems.length > 0 ||
    packageRows.length > 0 ||
    hasContent(legalNote);

  if (loading) {
    return (
      <div className="gpps-shell">
        <div className="gpps-grid">
          <div className="gpps-skel-image" />
          <div className="gpps-skel-info">
            <div className="gpps-skel-line gpps-skel-line-eyebrow" />
            <div className="gpps-skel-line gpps-skel-line-title" />
            <div className="gpps-skel-line gpps-skel-line-title gpps-skel-line-title-2" />
            <div className="gpps-skel-line gpps-skel-line-rating" />
            <div className="gpps-skel-line gpps-skel-line-price" />
            <div className="gpps-skel-line gpps-skel-line-text" />
            <div className="gpps-skel-line gpps-skel-line-text" />
            <div className="gpps-skel-line gpps-skel-line-text gpps-skel-line-text-short" />
            <div className="gpps-skel-actions">
              <div className="gpps-skel-btn" />
              <div className="gpps-skel-btn gpps-skel-btn-sm" />
            </div>
          </div>
        </div>
        {productPageStyles}
      </div>
    );
  }

  if ((error && _.isEmpty(singleProduct)) || (!singleProduct && !loading)) {
    return (
      <div className="gpps-shell">
        <ComponentError
          heading="Cannot load the product"
          description="The link may be broken or the product no longer exists."
        />
        {productPageStyles}
      </div>
    );
  }

  return (
    <div className="gpps-shell">
      {/* BREADCRUMB */}
      <nav className="gpps-crumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        <Link to="/shop">Shop</Link>
        <span aria-hidden="true">/</span>
        <span className="gpps-crumbs-current">{singleProduct?.title}</span>
      </nav>

      <div className="gpps-grid">
        {/* IMAGE GALLERY */}
        <div className="gpps-image-col">
          <div className="gpps-image-wrap">
            {singleProduct?.badges?.length > 0 && (
              <ul className="gpps-badges">
                {singleProduct.badges.map((badge, i) => (
                  <li key={badge + i} className="gpps-badge">
                    {badge}
                  </li>
                ))}
              </ul>
            )}
            {!_.isEmpty(singleProduct?.proimg) ? (
              <ReactImageGallery
                showPlayButton={false}
                autoPlay={false}
                showFullscreenButton={false}
                showNav
                items={singleProduct?.proimg?.map((image) => ({
                  original: image?.src,
                  thumbnail: image?.src,
                  originalClass: "gpps-gallery-original",
                  thumbnailClass: "gpps-gallery-thumb",
                }))}
              />
            ) : (
              <div className="gpps-image-placeholder" aria-hidden="true">
                ★
              </div>
            )}
          </div>
        </div>

        {/* INFO */}
        <div className="gpps-info-col">
          <div className="gpps-info">
            {/* Title */}
            <h1 className="gpps-title">{singleProduct?.title}</h1>

            {/* Rating */}
            <div className="gpps-rating">
              <Rating
                name="read-only"
                precision={0.5}
                value={Number(singleProduct?.five_start_rating) || 0}
                readOnly
                size="small"
                sx={{
                  "& .MuiRating-iconFilled": { color: "#d4a857" },
                  "& .MuiRating-iconEmpty": { color: "#3a2f1c" },
                }}
              />
              <span className="gpps-rating-num">
                {Number(singleProduct?.five_start_rating) || 0}
              </span>
              <span className="gpps-rating-divider" aria-hidden="true">
                ·
              </span>
              <span className="gpps-rating-label">verified buyer rating</span>
            </div>

            {/* Price */}
            <div className="gpps-price-row">
              <div>
                <span className="gpps-price-label">Price</span>
                <div className="gpps-price">
                  {currencyPrefix}
                  {singleProduct?.price}
                </div>
              </div>
            </div>

            {/* Description */}
            {descParagraphs.length > 0 && (
              <div className="gpps-desc">
                {descParagraphs.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}

            {/* CTAs */}
            <div className="gpps-actions">
              <button
                type="button"
                className="gpps-buy-btn"
                onClick={handleBuyNow}
                disabled={cartLoading}
              >
                {cartLoading ? (
                  <MoonLoader size={16} color="#1a1208" />
                ) : (
                  <BuyNowIcon />
                )}
                Buy Now
              </button>

              {inCart ? (
                <Grid className="gpps-qty" container>
                  <button
                    type="button"
                    onClick={handleDecrementQuantity}
                    disabled={cartLoading}
                    aria-label="Decrease quantity"
                  >
                    {cartLoading ? (
                      <MoonLoader size={14} color="#d4a857" />
                    ) : inCart.qty === 1 ? (
                      <DeleteIcon />
                    ) : (
                      "−"
                    )}
                  </button>
                  <span className="gpps-qty-val">{inCart?.qty}</span>
                  <button
                    type="button"
                    onClick={handleIncrementQuantity}
                    disabled={cartLoading}
                    aria-label="Increase quantity"
                  >
                    {cartLoading ? (
                      <MoonLoader size={14} color="#d4a857" />
                    ) : (
                      "+"
                    )}
                  </button>
                </Grid>
              ) : (
                <button
                  type="button"
                  className="gpps-cart-btn"
                  onClick={handleAddToCart}
                  disabled={cartLoading}
                  aria-label="Add to cart"
                >
                  {cartLoading ? (
                    <MoonLoader size={14} color="#d4a857" />
                  ) : (
                    <>
                      <i className="icon-cart" />
                      <span>Add to cart</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Trust badges */}
            <ul className="gpps-trust">
              {TRUST_BADGES.map((b) => (
                <li key={b.title} className="gpps-trust-item">
                  <span className="gpps-trust-icon">{b.icon}</span>
                  <div>
                    <strong>{b.title}</strong>
                    <small>{b.sub}</small>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* DETAILS BLOCK — only renders if at least one section has data */}
      {hasDetailsBlock && (
        <div className="gpps-details">
          <div className="gpps-details-head">
            <div className="gpps-details-eyebrow">
              <span className="gpps-details-dot" />
              EXPERIENCE DETAILS
            </div>
            <h2 className="gpps-details-title">
              Everything in your <span>package</span>
            </h2>
          </div>

          <div className="gpps-details-grid">
            {/* What's Included */}
            {includedItems.length > 0 && (
              <article className="gpps-detail-card">
                <div className="gpps-detail-head">
                  <span className="gpps-detail-icon gpps-detail-icon-good">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  </span>
                  <h3>What's Included</h3>
                </div>
                <ul className="gpps-detail-list">
                  {includedItems.map((item, i) => (
                    <li key={i} className="gpps-li-good">
                      <span className="gpps-li-mark">
                        <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            )}

            {/* What's Not Included */}
            {notIncludedItems.length > 0 && (
              <article className="gpps-detail-card">
                <div className="gpps-detail-head">
                  <span className="gpps-detail-icon gpps-detail-icon-bad">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </span>
                  <h3>What's Not Included</h3>
                </div>
                <ul className="gpps-detail-list">
                  {notIncludedItems.map((item, i) => (
                    <li key={i} className="gpps-li-bad">
                      <span className="gpps-li-mark">
                        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            )}

            {/* Package Details */}
            {packageRows.length > 0 && (
              <article className="gpps-detail-card gpps-detail-card-wide">
                <div className="gpps-detail-head">
                  <span className="gpps-detail-icon">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M20 6h-2.18c.11-.31.18-.65.18-1a3 3 0 00-5.5-1.65l-.5.67-.5-.68A2.99 2.99 0 009 2 3 3 0 006 5c0 .35.07.69.18 1H4a2 2 0 00-1.99 2L2 19a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2zM9 4a1 1 0 110 2 1 1 0 010-2zm6 0a1 1 0 110 2 1 1 0 010-2zm5 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 12 7.4l3.38 4.6L17 10.83 14.92 8H20v6z" />
                    </svg>
                  </span>
                  <h3>Package Details</h3>
                </div>
                <dl className="gpps-pkg-grid">
                  {packageRows.map((row, i) => (
                    <div className="gpps-pkg-row" key={i}>
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            )}

            {/* Legal note */}
            {hasContent(legalNote) && (
              <article className="gpps-detail-card gpps-detail-card-wide gpps-legal">
                <div className="gpps-legal-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12 2L1 21h22L12 2zm-1 8h2v6h-2v-6zm0 8h2v2h-2v-2z" />
                  </svg>
                </div>
                <div>
                  <strong>Important note</strong>
                  <p>{legalNote}</p>
                </div>
              </article>
            )}
          </div>
        </div>
      )}

      {productPageStyles}
    </div>
  );
};

export default Product;

const productPageStyles = (
  <style>{`
    .gpps-shell {
      max-width: 1300px;
      margin: 0 auto;
      padding: 30px 22px 80px;
      position: relative;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }

    /* BREADCRUMB */
    .gpps-crumbs {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      font-size: 12px;
      color: #9aa0a8;
      margin-bottom: 22px;
      padding: 8px 0;
    }
    .gpps-crumbs a {
      color: #9aa0a8;
      text-decoration: none;
      transition: color .2s ease;
    }
    .gpps-crumbs a:hover { color: #d4a857; text-decoration: none; }
    .gpps-crumbs span { color: #4b5563; }
    .gpps-crumbs-current {
      color: #d4a857 !important;
      font-weight: 600;
      max-width: 280px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* GRID */
    .gpps-grid {
      display: grid;
      grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
      gap: 36px;
      align-items: flex-start;
    }

    /* IMAGE COL */
    .gpps-image-col { position: relative; }
    .gpps-image-wrap {
      position: relative;
      background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
      border: 1px solid rgba(212, 168, 87, 0.25);
      border-radius: 18px;
      padding: 18px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
      overflow: hidden;
    }
    .gpps-badges {
      position: absolute;
      top: 14px;
      left: 14px;
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      z-index: 3;
    }
    .gpps-badge {
      background: linear-gradient(135deg, #d4a857, #b58a3f);
      color: #1a1208;
      font-weight: 700;
      font-size: 10px;
      letter-spacing: 0.5px;
      padding: 4px 10px;
      border-radius: 999px;
      box-shadow: 0 4px 10px rgba(212, 168, 87, 0.3);
      text-transform: uppercase;
    }

    .gpps-image-placeholder {
      aspect-ratio: 1 / 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(212, 168, 87, 0.3);
      font-size: 80px;
    }

    /* React Image Gallery overrides */
    .gpps-image-wrap .image-gallery,
    .gpps-image-wrap .image-gallery-content { background: transparent !important; }
    .gpps-image-wrap .image-gallery-image,
    .gpps-image-wrap .gpps-gallery-original {
      max-height: 460px !important;
      object-fit: contain !important;
      background: transparent;
    }
    .gpps-image-wrap .image-gallery-slide {
      background: #0b0b0c;
      border-radius: 12px;
      overflow: hidden;
      border: 1px solid rgba(212, 168, 87, 0.12);
    }
    .gpps-image-wrap .image-gallery-thumbnails-wrapper { margin-top: 14px !important; }
    .gpps-image-wrap .image-gallery-thumbnail {
      border: 2px solid rgba(212, 168, 87, 0.18) !important;
      border-radius: 10px !important;
      overflow: hidden;
      background: #0b0b0c;
      transition: border-color .25s ease, transform .25s ease !important;
    }
    .gpps-image-wrap .image-gallery-thumbnail:hover {
      border-color: rgba(212, 168, 87, 0.5) !important;
      transform: translateY(-2px);
    }
    .gpps-image-wrap .image-gallery-thumbnail.active,
    .gpps-image-wrap .image-gallery-thumbnail:focus {
      border-color: #d4a857 !important;
      box-shadow: 0 4px 12px rgba(212, 168, 87, 0.3);
    }
    .gpps-image-wrap .image-gallery-thumbnail-image {
      object-fit: cover !important;
      max-height: 70px !important;
    }
    .gpps-image-wrap .image-gallery-icon {
      color: #d4a857 !important;
      filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.6));
      transition: transform .2s ease, color .2s ease;
    }
    .gpps-image-wrap .image-gallery-icon:hover { color: #f1cf85 !important; transform: scale(1.1); }
    .gpps-image-wrap .image-gallery-left-nav,
    .gpps-image-wrap .image-gallery-right-nav { padding: 0 16px !important; }

    /* INFO COL */
    .gpps-info-col { position: sticky; top: 96px; }
    .gpps-info {
      background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
      border: 1px solid rgba(212, 168, 87, 0.2);
      border-radius: 18px;
      padding: 28px 30px 26px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
    }

    .gpps-meta { display: flex; flex-wrap: wrap; gap: 8px; }
    .gpps-meta-chip {
      display: inline-flex;
      align-items: center;
      padding: 5px 12px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(212, 168, 87, 0.2);
      color: #cfd2d6;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.4px;
      text-decoration: none;
      transition: background .2s ease, color .2s ease, border-color .2s ease;
    }
    .gpps-meta-chip:hover {
      background: rgba(212, 168, 87, 0.12);
      color: #d4a857;
      border-color: rgba(212, 168, 87, 0.45);
      text-decoration: none;
    }
    .gpps-meta-chip-brand {
      color: #d4a857;
      background: rgba(212, 168, 87, 0.1);
      border-color: rgba(212, 168, 87, 0.35);
      font-weight: 700;
      letter-spacing: 0.6px;
      text-transform: uppercase;
    }

    .gpps-title {
      font-family: "Playfair Display", "Times New Roman", serif;
      font-size: 30px;
      font-weight: 700;
      color: #fff;
      margin: 0;
      line-height: 1.2;
      letter-spacing: -0.3px;
    }

    .gpps-rating {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12.5px;
      color: #9aa0a8;
    }
    .gpps-rating-num { color: #d4a857; font-weight: 700; }
    .gpps-rating-divider { color: #4b5563; }
    .gpps-rating-label { color: #9aa0a8; }

    .gpps-price-row {
      display: flex;
      align-items: flex-end;
      gap: 18px;
      padding: 16px 0 14px;
      border-top: 1px solid rgba(212, 168, 87, 0.12);
      border-bottom: 1px solid rgba(212, 168, 87, 0.12);
      flex-wrap: wrap;
    }
    .gpps-price-label,
    .gpps-price-old-label {
      display: block;
      font-size: 10px;
      color: #6b7280;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-bottom: 4px;
      font-weight: 700;
    }
    .gpps-price {
      font-size: 32px;
      font-weight: 800;
      background: linear-gradient(135deg, #f1cf85, #b58a3f);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      line-height: 1;
      letter-spacing: -0.5px;
    }
    .gpps-price-old-wrap { display: inline-flex; flex-direction: column; }
    .gpps-price-old {
      font-size: 16px;
      color: #6b7280;
      text-decoration: line-through;
      font-weight: 600;
    }

    .gpps-stock {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .gpps-stock-row {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      font-weight: 500;
    }
    .gpps-stock-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .gpps-stock-high .gpps-stock-dot {
      background: #00d488;
      box-shadow: 0 0 8px rgba(0, 212, 136, 0.6);
    }
    .gpps-stock-high .gpps-stock-label { color: #00d488; }
    .gpps-stock-med .gpps-stock-dot { background: #f1cf85; box-shadow: 0 0 8px rgba(241, 207, 133, 0.6); }
    .gpps-stock-med .gpps-stock-label { color: #f1cf85; }
    .gpps-stock-low .gpps-stock-dot {
      background: #ff8c5a;
      box-shadow: 0 0 8px rgba(255, 140, 90, 0.6);
      animation: gpps-stock-blink 1.4s ease-in-out infinite;
    }
    .gpps-stock-low .gpps-stock-label { color: #ff8c5a; }
    .gpps-stock-out .gpps-stock-dot { background: #ff5a5f; }
    .gpps-stock-out .gpps-stock-label { color: #ff5a5f; }
    @keyframes gpps-stock-blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.45; }
    }
    .gpps-stock-bar {
      height: 4px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 999px;
      overflow: hidden;
    }
    .gpps-stock-bar-fill {
      display: block;
      height: 100%;
      background: linear-gradient(90deg, #d4a857, #f1cf85);
      border-radius: 999px;
      transition: width .4s ease;
    }
    .gpps-stock-low .gpps-stock-bar-fill {
      background: linear-gradient(90deg, #ff8c5a, #f1cf85);
    }

    .gpps-desc {
      color: #cfd2d6;
      font-size: 14px;
      line-height: 1.65;
      margin: 0;
      max-height: 180px;
      overflow-y: auto;
      padding-right: 6px;
    }
    .gpps-desc::-webkit-scrollbar { width: 5px; }
    .gpps-desc::-webkit-scrollbar-thumb {
      background: rgba(212, 168, 87, 0.25);
      border-radius: 999px;
    }

    /* ACTIONS */
    .gpps-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }
    .gpps-buy-btn {
      flex: 1;
      min-width: 180px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      background: linear-gradient(135deg, #f1cf85 0%, #d4a857 50%, #b58a3f 100%);
      color: #1a1208;
      border: 1px solid rgba(255, 232, 178, 0.55);
      border-radius: 12px;
      padding: 14px 22px;
      font-size: 14px;
      font-weight: 800;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      cursor: pointer;
      box-shadow: 0 8px 22px rgba(212, 168, 87, 0.35);
      transition: transform .2s ease, filter .2s ease, box-shadow .2s ease;
      font-family: inherit;
    }
    .gpps-buy-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      filter: brightness(1.05);
      box-shadow: 0 12px 28px rgba(212, 168, 87, 0.45);
    }
    .gpps-buy-btn:disabled { opacity: 0.65; cursor: not-allowed; }

    .gpps-cart-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: transparent;
      border: 1px solid rgba(212, 168, 87, 0.4);
      color: #d4a857;
      padding: 14px 20px;
      border-radius: 12px;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      cursor: pointer;
      font-family: inherit;
      transition: background .2s ease, border-color .2s ease;
    }
    .gpps-cart-btn:hover:not(:disabled) {
      background: rgba(212, 168, 87, 0.12);
      border-color: #d4a857;
    }
    .gpps-cart-btn:disabled { opacity: 0.6; cursor: not-allowed; }

    .gpps-qty {
      display: inline-flex !important;
      align-items: center;
      background: rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(212, 168, 87, 0.4);
      border-radius: 12px;
      overflow: hidden;
      width: auto !important;
    }
    .gpps-qty button {
      background: transparent;
      border: none;
      color: #d4a857;
      width: 44px;
      height: 48px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background .15s ease;
      font-family: inherit;
    }
    .gpps-qty button:hover:not(:disabled) {
      background: rgba(212, 168, 87, 0.15);
    }
    .gpps-qty button:disabled { opacity: 0.6; cursor: not-allowed; }
    .gpps-qty-val {
      color: #fff;
      font-size: 15px;
      font-weight: 700;
      min-width: 40px;
      text-align: center;
      padding: 0 6px;
    }

    /* TRUST */
    .gpps-trust {
      list-style: none;
      padding: 16px 0 0;
      margin: 4px 0 0;
      border-top: 1px dashed rgba(212, 168, 87, 0.2);
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    .gpps-trust-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }
    .gpps-trust-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: rgba(212, 168, 87, 0.12);
      color: #d4a857;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      flex-shrink: 0;
    }
    .gpps-trust-item strong {
      display: block;
      font-size: 12.5px;
      color: #fff;
      font-weight: 600;
      margin-bottom: 2px;
    }
    .gpps-trust-item small {
      font-size: 11px;
      color: #9aa0a8;
      line-height: 1.4;
    }

    /* ============ SKELETON ============ */
    .gpps-skel-image {
      aspect-ratio: 1 / 1;
      max-height: 540px;
      border-radius: 18px;
      background: linear-gradient(90deg, #15110a 0%, #1a1208 50%, #15110a 100%);
      background-size: 200% 100%;
      animation: gpps-shimmer 1.4s linear infinite;
      border: 1px solid rgba(212, 168, 87, 0.12);
    }
    .gpps-skel-info {
      background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
      border: 1px solid rgba(212, 168, 87, 0.12);
      border-radius: 18px;
      padding: 28px 30px;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    .gpps-skel-line {
      height: 14px;
      border-radius: 4px;
      background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
      background-size: 200% 100%;
      animation: gpps-shimmer 1.4s linear infinite;
    }
    .gpps-skel-line-eyebrow { width: 30%; height: 10px; }
    .gpps-skel-line-title { height: 24px; width: 90%; }
    .gpps-skel-line-title-2 { width: 65%; }
    .gpps-skel-line-rating { width: 45%; height: 12px; }
    .gpps-skel-line-price { width: 50%; height: 28px; }
    .gpps-skel-line-text { width: 100%; }
    .gpps-skel-line-text-short { width: 70%; }
    .gpps-skel-actions { display: flex; gap: 10px; margin-top: 6px; }
    .gpps-skel-btn {
      height: 48px;
      border-radius: 12px;
      flex: 1;
      background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
      background-size: 200% 100%;
      animation: gpps-shimmer 1.4s linear infinite;
    }
    .gpps-skel-btn-sm { flex: 0 0 100px; }
    @keyframes gpps-shimmer {
      from { background-position: 200% 0; }
      to { background-position: -200% 0; }
    }

    /* ============ DETAILS BLOCK ============ */
    .gpps-desc { display: flex; flex-direction: column; gap: 8px; }
    .gpps-desc p { margin: 0; }

    .gpps-details {
      margin-top: 36px;
      padding: 30px 30px 28px;
      background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
      border: 1px solid rgba(212, 168, 87, 0.22);
      border-radius: 18px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
    }
    .gpps-details-head {
      text-align: center;
      margin-bottom: 24px;
      padding-bottom: 18px;
      border-bottom: 1px dashed rgba(212, 168, 87, 0.2);
    }
    .gpps-details-eyebrow {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 5px 12px;
      background: rgba(212, 168, 87, 0.1);
      border: 1px solid rgba(212, 168, 87, 0.3);
      color: #d4a857;
      border-radius: 999px;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 1.8px;
      margin-bottom: 12px;
    }
    .gpps-details-dot {
      width: 5px; height: 5px;
      border-radius: 50%;
      background: #d4a857;
      box-shadow: 0 0 8px #d4a857;
      animation: gpps-stock-blink 1.6s ease-in-out infinite;
    }
    .gpps-details-title {
      font-family: "Playfair Display", "Times New Roman", serif;
      font-size: 26px;
      color: #fff;
      font-weight: 700;
      margin: 0;
      line-height: 1.25;
    }
    .gpps-details-title span {
      background: linear-gradient(135deg, #f1cf85, #b58a3f);
      -webkit-background-clip: text; background-clip: text; color: transparent;
      font-style: italic; font-weight: 600;
    }

    .gpps-details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .gpps-detail-card {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(212, 168, 87, 0.18);
      border-radius: 14px;
      padding: 22px;
      transition: border-color .25s ease;
    }
    .gpps-detail-card:hover { border-color: rgba(212, 168, 87, 0.4); }
    .gpps-detail-card-wide { grid-column: 1 / -1; }

    .gpps-detail-head {
      display: flex; align-items: center; gap: 12px;
      margin-bottom: 14px;
      padding-bottom: 12px;
      border-bottom: 1px solid rgba(212, 168, 87, 0.14);
    }
    .gpps-detail-head h3 {
      font-family: "Playfair Display", serif;
      font-size: 17px; color: #fff; margin: 0; font-weight: 700;
    }
    .gpps-detail-icon {
      width: 36px; height: 36px;
      background: rgba(212, 168, 87, 0.12);
      border: 1px solid rgba(212, 168, 87, 0.3);
      color: #d4a857;
      border-radius: 10px;
      display: inline-flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .gpps-detail-icon-good {
      background: rgba(74, 222, 128, 0.12);
      border-color: rgba(74, 222, 128, 0.32);
      color: #4ade80;
    }
    .gpps-detail-icon-bad {
      background: rgba(255, 90, 90, 0.1);
      border-color: rgba(255, 90, 90, 0.3);
      color: #ff7a7a;
    }

    .gpps-detail-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex; flex-direction: column; gap: 8px;
    }
    .gpps-detail-list li {
      display: flex; align-items: flex-start; gap: 10px;
      font-size: 13.5px;
      color: #c5c8cf;
      line-height: 1.55;
    }
    .gpps-li-mark {
      width: 18px; height: 18px;
      border-radius: 50%;
      display: inline-flex; align-items: center; justify-content: center;
      flex-shrink: 0;
      margin-top: 1px;
    }
    .gpps-li-good .gpps-li-mark {
      background: rgba(74, 222, 128, 0.15);
      border: 1px solid rgba(74, 222, 128, 0.32);
      color: #4ade80;
    }
    .gpps-li-bad .gpps-li-mark {
      background: rgba(255, 90, 90, 0.12);
      border: 1px solid rgba(255, 90, 90, 0.3);
      color: #ff7a7a;
    }

    .gpps-pkg-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
      margin: 0;
    }
    .gpps-pkg-row {
      display: flex; flex-direction: column; gap: 4px;
      padding: 12px 14px;
      background: rgba(0, 0, 0, 0.25);
      border: 1px solid rgba(212, 168, 87, 0.14);
      border-radius: 10px;
      transition: border-color .2s ease, background .2s ease;
    }
    .gpps-pkg-row:hover {
      border-color: rgba(212, 168, 87, 0.32);
      background: rgba(212, 168, 87, 0.04);
    }
    .gpps-pkg-row dt {
      font-size: 10.5px;
      color: #d4a857;
      font-weight: 700;
      letter-spacing: 1.4px;
      text-transform: uppercase;
      margin: 0;
    }
    .gpps-pkg-row dd {
      font-size: 13.5px;
      color: #fff;
      font-weight: 600;
      margin: 0;
      line-height: 1.45;
    }

    .gpps-legal {
      display: grid;
      grid-template-columns: 44px 1fr;
      gap: 14px;
      align-items: flex-start;
      background: rgba(212, 168, 87, 0.06);
      border: 1px solid rgba(212, 168, 87, 0.28);
      border-left: 3px solid #d4a857;
    }
    .gpps-legal-icon {
      width: 44px; height: 44px;
      background: rgba(212, 168, 87, 0.14);
      border: 1px solid rgba(212, 168, 87, 0.35);
      color: #d4a857;
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .gpps-legal strong {
      display: block;
      font-family: "Playfair Display", serif;
      font-size: 14px;
      color: #d4a857;
      font-weight: 700;
      margin-bottom: 4px;
      letter-spacing: 0.3px;
    }
    .gpps-legal p {
      font-size: 13.5px;
      color: #c5c8cf;
      line-height: 1.6;
      margin: 0;
    }

    /* ============ RESPONSIVE ============ */
    @media (max-width: 991px) {
      .gpps-grid { grid-template-columns: 1fr; gap: 22px; }
      .gpps-info-col { position: static; }
    }
    @media (max-width: 640px) {
      .gpps-shell { padding: 20px 14px 60px; }
      .gpps-image-wrap { padding: 12px; border-radius: 14px; }
      .gpps-image-wrap .image-gallery-image,
      .gpps-image-wrap .gpps-gallery-original { max-height: 320px !important; }
      .gpps-info { padding: 22px 20px; border-radius: 14px; }
      .gpps-title { font-size: 22px; }
      .gpps-price { font-size: 26px; }
      .gpps-trust { grid-template-columns: 1fr; }
      .gpps-buy-btn { min-width: 0; padding: 13px 18px; font-size: 13px; }
      .gpps-cart-btn { padding: 13px 16px; font-size: 12px; }
      .gpps-crumbs { font-size: 11px; }
      .gpps-crumbs-current { max-width: 160px; }
      .gpps-details { padding: 22px 20px; }
      .gpps-details-title { font-size: 22px; }
      .gpps-details-grid { grid-template-columns: 1fr; gap: 12px; }
      .gpps-pkg-grid { grid-template-columns: 1fr; }
    }
  `}</style>
);
